<template>
  <div class="w-full flex flex-col">
    <div class="p-2 bg-white rounded shadow flex flex-col" v-if="loading">
      <Loading />
    </div>
    <slot name="top"></slot>
    <div class="
        absolute
        top-2
        left-1/2
        transform
        translate-x-1/2
        bg-gray-50
        rounded
        p-2
        shadow
        flex flex-row
        items-center
      " v-if="!!currentRoute?.query?.linkedFrom">
      <router-link class="p-2 text- text-sm font-medium flex
        cursor-pointer hover:underline" :to="currentRoute?.query?.linkedFrom">Go back to {{
        currentRoute.query.linkedFromNumber }}</router-link>
      <font-awesome-icon class="text-cancel ml-4 cursor-pointer" icon="times"
        @click="router.replace({ 'query': null })"></font-awesome-icon>
    </div>
    <div class="m-2 p-2 bg-white rounded shadow flex flex-col" v-if="!!itemOverride">
      <Fields :title="route.meta.title(itemOverride || {}, contextData)"
        :contextData="{ ...contextData, item: itemOverride }" :dataFields="dataFields" :menuActions="menuActions"
        :hideTopComp="false" :hideBackButton="route.meta.hideBackButton">
        <template #topRight>
          <FavouriteStar :pageItem="itemOverride" :contextData="contextData" />
        </template>
      </Fields>
    </div>
    <slot name="mid"></slot>
    <div v-if="loaded">
      <div class="m-2 bg-white rounded shadow flex flex-col" v-show="!loading">
        <AttachableTables ref="tables" :tables="aiTree" :pageItem="itemOverride || item" :contextData="contextData"
          :selectedTableIndex="selectedTableIndex" @selectedtableindexchanged="selectedTableIndexChanged" />
      </div>
    </div>
    <br />
    <slot name="bottom"></slot>
  </div>
  <DynamicForm ref="form" @submitted="formSubmitted" />
</template>
<script setup>
// import all the vue stuff
import { useStore } from "vuex";
import { useRoute, useRouter } from "vue-router";
import { computed, onMounted, ref, watch, onBeforeMount, onBeforeUnmount, inject } from "vue";
// load firebase helper
import { db } from "../../db.js";
// Load components
import { Loading, IconButton } from "@comp/index.js";
import AttachableTables from "../AttachableTables/AttachableTables.vue";
import DynamicForm from "../DynamicForm/DynamicForm.vue";
import Fields from "../Fields/Fields.vue";
import FavouriteStar from "./FavouriteStar.vue";
// extra
import { get, has } from "lodash";

import standardMenuActions from "../../views/standardMenuActions";
import { addToRecents } from "../../utils.js";
import { useToast } from "vue-toastification";
import { resolveRouteTitle } from "./routeMetaTitle.js";
import {
  buildSettingsEntry,
  hasSettingsIdentity,
  normalizeSettingsList,
  sameSettingsEntry,
  sanitizeSettingsList,
} from "./userdataSettings.js";

// inistalise vue objects
const store = useStore();
const route = useRoute();
const router = useRouter();
const toast = useToast();
let routeMeta = ref(null);

// Optional injections for route-specific modals/components
const proposeTagsModal = inject('proposeTagsModal', null);
// make them accessable in the template
const currentRoute = computed(() => route);
const dataFields = computed(() => get(routeMeta.value, "dataFields"));
const aiTree = computed(() => get(routeMeta.value, "attachedItems"));
const menuActions = computed(() => {
  if (routeMeta.value?.readOnly) return [];
  let ma = get(routeMeta.value, "menuActions", []);
  return [...ma, standardMenuActions.openInFirebase];
});

// set the component refs
let contextMenu = ref(null);
let form = ref(null);
let tables = ref(null);
let topComponent = ref(null);
let selectedTableIndex = ref(100);
let showCancelled = ref(false);

// init page item and attached items
let loaded = ref(false);
let loading = ref(true);

const props = defineProps({
  // allow passing in an item directly, this is used for the linked items functionality
  itemOverride: Object,
});

// get the path for the ltem data location
let itemLocation = ref(route.path);

// A function override lets a route resolve its data path dynamically (and possibly
// asynchronously), e.g. a self-service view looking up the logged-in user's own record.
async function resolveItemLocation() {
  const override = currentRoute.value.meta.dataPathOverride;
  if (!override) {
    itemLocation.value = currentRoute.value.path;
  } else if (typeof override === "function") {
    itemLocation.value = await override(currentRoute.value, baseContext.value);
  } else {
    
    itemLocation.value = override.replace(":id", currentRoute.value.params.id);
  }
  
  return itemLocation.value;
}

let item = computed(() => {
  return store.state[itemLocation.value];
});

let attachedItems = ref({});

const baseContext = computed(() => {
  const context = {
    currentRoute: currentRoute.value,
    form: form.value,
    contextMenu: contextMenu.value,
    store,
    router,
    db,
    toast,
    meta: routeMeta?.value?.formMetadata,
  };
  
  // Add optional injected components
  if (proposeTagsModal?.value) {
    context.proposeTagsModal = proposeTagsModal.value;
  }
  
  return context;
});
const linkedDataContext = baseContext;
const itemLinkedData = ref(null);

let itemOverride = computed(() => {

  if(props.itemOverride) return props.itemOverride;
  
  let io = currentRoute.value.meta.itemOverride
    ? currentRoute.value.meta.itemOverride(item.value, baseContext.value)
    : item.value;

  const linkedData = currentRoute.value.meta.linkedData?.(linkedDataContext.value) || itemLinkedData.value;
  if (linkedData) {
    return {
      ...io,
      ...linkedData,
    };
  } else {
    return io;
  }
});

// init context data, this object is passed to attached item configs and actions
let contextData = computed(() => ({
  ...baseContext.value,
  pageItem: itemOverride.value,
  attachedItems: tables?.value?.getAttachedItems(),
}));


async function formSubmitted(dynamicForm) {
  loaded.value = false;
  await wait(150);
  loaded.value = true;
  tables.value?.recalculateWarnings();
  // do not add this is the form type was delete
  
  if(dynamicForm?.config?.type !== "delete") {
    await db.set(`${itemLocation.value}/__lastViewed`, new Date().toISOString());
  }
  if(currentRoute.value.meta.onFormSubmitted) {
    await currentRoute.value.meta.onFormSubmitted(contextData.value);
  }
  await loadLinkedData();
};

async function watchItem() {
  if (currentRoute.value.meta?.watch === false) return;
  
  return db.watch(itemLocation.value, store);
}

const linkedDataWatchPaths = new Set();
let linkedDataWatchVersion = 0;

function clearLinkedDataWatches() {
  linkedDataWatchVersion++;
  linkedDataWatchPaths.forEach(path => db.unwatch(path));
  linkedDataWatchPaths.clear();
}

async function watchLinkedData() {
  const getLocations = currentRoute.value.meta?.linkedDataWatchLocations;
  if (!getLocations) {
    clearLinkedDataWatches();
    return true;
  }

  if (!contextData.value.pageItem?.ID) {
    await wait(100);
    return false;
  }

  const paths = new Set(getLocations(linkedDataContext.value).filter(Boolean));
  linkedDataWatchPaths.forEach(path => {
    if (paths.has(path)) return;
    db.unwatch(path);
    linkedDataWatchPaths.delete(path);
  });

  const newPaths = [...paths].filter(path => !linkedDataWatchPaths.has(path));
  newPaths.forEach(path => linkedDataWatchPaths.add(path));
  const watchVersion = ++linkedDataWatchVersion;
  await Promise.all(newPaths.map(path => db.watch(path, store)));
  return watchVersion === linkedDataWatchVersion;
}

async function loadLinkedData() {
  if (currentRoute.value.meta?.linkedDataWatchLocations) {
    itemLinkedData.value = null;
    return watchLinkedData();
  }

  clearLinkedDataWatches();
  if (!currentRoute.value.meta?.getLinkedData) {
    itemLinkedData.value = null;
    return true;
  }

  if (!contextData.value.pageItem?.ID) {
    await wait(100);
    return false;
  }

  itemLinkedData.value = await currentRoute.value.meta.getLinkedData(contextData.value);
  return true;
}

function addItemToRecents() {

  // if (currentRoute.value.meta?.externalSelf === true) return;

  addToRecents(contextData.value);
  addItemToSettingsRecents();
}

async function addItemToSettingsRecents() {
  // Do not record list views
  if (currentRoute.value.meta?.addToSettingsRecents === false) return;
  if (currentRoute.value.meta?.isListView) return;
  
  const pageItem = itemOverride.value;
  const currentPath = currentRoute.value.path;
  const currentCollection = currentRoute.value.meta?.dataPath;
  const currentId = pageItem?.ID || currentRoute.value.params.id;
  const meta = currentRoute.value.meta;
  
  // Skip if there's no path/id to reopen
  if (!currentPath || !currentId) return;
  
  let recents = normalizeSettingsList(store.state.user?.Settings?.recents);

  // Generate title (same logic as FavouriteStar). Pass route context so
  // meta.title can read router without throwing when a caller omits it.
  const title = resolveRouteTitle(meta, pageItem, contextData.value);
  
  const newRecent = buildSettingsEntry({
    path: currentPath,
    collection: currentCollection,
    id: currentId,
    title: title,
    type: meta.name,
    section: meta.app,
    time: new Date().toISOString()
  });

  if (!hasSettingsIdentity(newRecent)) return;
  
  // Remove existing entry (dedup by collection+id or path)
  recents = recents.filter(item => !sameSettingsEntry(item, {
    collection: currentCollection,
    id: currentId,
    path: currentPath
  }));
  
  // Prepend new entry (most recent first) and cap at 50
  recents = sanitizeSettingsList([newRecent, ...recents]).slice(0, 50);
  
  // Persist to Firebase. Skip when the userdata key is missing.
  const uid = store.getters.userID;
  if (uid) {
    await db.set(`Userdata/${uid}/Settings/recents`, recents);
  }
}

function loadAttachedItems() {
  loading.value = false;
  if (!has(routeMeta.value, "attachedItems")) return false;

  loaded.value = true;
  return true;
}

async function getItemAndAttachedItems(time = 0) {

  try {
    await getLazyMeta();
    await resolveItemLocation();

    // set loading to true
    loading.value = true;
    await wait(time);
    await watchItem();


    const linkedDataLoaded = await loadLinkedData();
    if (!linkedDataLoaded) {
      getItemAndAttachedItems();
      return;
    }
    if(routeMeta.value?.addToRecents !== false){
      addItemToRecents();
    }
    loadAttachedItems();
  } catch (err) {
    console.warn(err);
  }

  setTimeout(() => {
    loading.value = false;
  }, 8000);
}

const selectedTableIndexChanged = (number) => {
  selectedTableIndex.value = number;
};

function wait(time = 0) {
  return new Promise((res) => {
    setTimeout(res, time);
  });
}

async function getLazyMeta() {
  // let meta = !!currentRoute.value.meta.lazyLoadMeta ? await currentRoute.value.meta.lazyLoadMeta() : {};
  routeMeta.value = {
    ...currentRoute.value.meta
  };
}

const linkedDataWatchKey = computed(() => {
  const getLocations = routeMeta.value?.linkedDataWatchLocations;
  if (!getLocations) return null;
  return [...new Set(getLocations(linkedDataContext.value).filter(Boolean))].sort().join("\n");
});

watch(() => [route.path, route.params], getItemAndAttachedItems);
watch(linkedDataWatchKey, async (next, previous) => {
  if (previous === undefined || next === previous) return;
  try {
    await watchLinkedData();
  } catch (err) {
    console.warn(err);
  }
});
watch(item, () => {
  // The profile page renders the live user record and does not subscribe itself.
  // Reloading here writes recents back into that same record and starts again.
  if (currentRoute.value.meta?.watch === false) return;
  loaded.value = false;


  getItemAndAttachedItems();
});
// watch(store.state, getItemAndAttachedItems);
onBeforeMount(async () => {
  if (currentRoute.value.meta.onBeforeMount) {
    await currentRoute.value.meta.onBeforeMount();
  }
  await resolveItemLocation();
  if (currentRoute.value.meta.onRouteEnter) {
    currentRoute.value.meta.onRouteEnter(contextData.value);
  }
  await getLazyMeta();
});
onMounted(getItemAndAttachedItems);
onBeforeUnmount(clearLinkedDataWatches);

defineExpose({
  form,
  item,
  attachedItems,
  contextData,
  loaded,
  resolveItemLocation,
  getLazyMeta,
  watchItem,
  loadLinkedData,
  watchLinkedData,
  clearLinkedDataWatches,
  addItemToRecents,
  addItemToSettingsRecents,
  loadAttachedItems,
  getItemAndAttachedItems
});

</script>
